import { BackendEvent, EventAttendee, FrontendEvent, AdminEvent, Ticket, ExistingTicketHolder, EventAttendeesPagination, IEventMetrics } from 'common/types'
import { TicketOptions } from 'common/types/ticket.types'
import { useHttp } from 'hooks/use-http/useHttp'
import {useState} from 'react'
import { MealAttendee, MealTimes} from '../../pages/summit/bfutr2023/types/index'

export const useEventApi = () => {
  const { get, post, put, destroy } = useHttp()
  const [isLoading, setIsLoading] =useState<boolean>(true)
  const fetchFilteredEvents = async (filter?: string, page = 1, pageSize: Number = 150) => {
      const response = await get(
        `event/?page=${page}&pageSize=${pageSize}&dir=asc&sort=date${filter}&searchCriteria=`,
        {
          useApiKey: true,
          dieSilent: true
        }
      )
      return response
  }

  const fetchEventsWithRegistrant = async (
    status?: string[],
    fromDate?: string,
    toDate?: string,
    searchCriteria?: string,
    type?: string[],
    page = 1,
    pageSize: Number = 200
  ) => {
    const response: FrontendEvent = await get(
      `event/admin/list?page=${page}&pageSize=${pageSize}&dir=desc&sort=e.created_at&status=${status}&searchCriteria=${searchCriteria}&fromDate=${fromDate}&toDate=${toDate}&type=${type}`,
      { dieSilent: true }
    )
    return response
  }

  const fetchRegisteredEvents = async (userId: string) => {
    const response: FrontendEvent = await get(`event/${userId}/user-events`, { useApiKey: true, dieSilent: true })
    return response
  }

  const fetchSummitBySubTitle = async (subtitle: string) => {
    const response: FrontendEvent = await get(`event/subtitle/${subtitle}`, { useApiKey: true, dieSilent: true })
    return response
  }

  const fetchEvent = async (id: string) => {
    const response: FrontendEvent = await get(`event/${id}`, { useApiKey: true, dieSilent: true })
    return response
  }

  const fetchSummitAttendeeAndTicket = async (userId: string, eventId: string) => {
    const response: ExistingTicketHolder = await get(`event/summit/${eventId}/attendee/${userId}`, { dieSilent: true })
    return response
  }

  const fetchSummitEvent = async () => {
    const response: FrontendEvent = await get(`event/summit/current`, { useApiKey: true, dieSilent: true })
    return response
  }


  const fetchEventWithApiKey = async (id: string) => {
    const response: BackendEvent = await get(`event/${id}`, { dieSilent: true, useApiKey: true })
    return response
  }

  const createEvent = async (event: AdminEvent, userId: string) => {
    const response: BackendEvent = await post(`event/${userId}`, event)
    if(response){
      setIsLoading(false)
      return response 
    }else{
      setIsLoading(true)
    }
    
  }


  const updateEvent = async (event: AdminEvent) => {
    const response: BackendEvent = await put(`event/${event.id}`, event, { dieSilent: true })
    if (response) {
      setIsLoading(false)
      return response
    } else {
      setIsLoading(true)
    }
    
  }

  const registerForEvent = async (userId: string, eventId: string, options: TicketOptions) => {
    const response: EventAttendee = await post(`event/${userId}/register/${eventId}`, options, { useApiKey: true })
    return response
  }

  const unRegisterForEvent = async (userId: string, eventId: string) => {
    const response: EventAttendee = await post(`event/${userId}/un-register/${eventId}`, { useApiKey: true })
    return response
  }

  const confirmEventAttendee = async (userId: string, eventId: string) => {
    const response: EventAttendee = await get(`event/${eventId}/user/${userId}`, { dieSilent: true })
    return response
  }

  const updateEventAttendee = async (eventAttendeeId: string, eventId: string, payload: Partial<EventAttendee>) => {
    const response: EventAttendee = await put(`/event/${eventId}/attendee/${eventAttendeeId}`, payload)
    return response
  }

  const loginEngageEvent = async (userId: string, eventId: string, showKey: string) => {
    const response = await get(`event/intrado/${userId}/login/${showKey}`)
    return response
  }

  const deleteEvent = async (eventId: string) => {
    return await destroy(`event/${eventId}`, { useApiKey: true })
  }

  const fetchEventCounts = async () => {
    const response: any = await get(`event/count/by-status`, { dieSilent: true, useApiKey: true })
    return response
  }

  const deleteBulkEvents = async (events: string[]) => {
    const response: any[] = await put(`event/delete/bulk`, { eventIds: events }, { dieSilent: true })
    return response
  }

  const getAvailableSeats = async (eventId: string) => {
    const response: any = await get(`event/${eventId}/available-seats`, { dieSilent: true, useApiKey: true })
    return response
  }

  const fetchEventAttendees = async (eventId: string, page = 1, pageSize = 10) => {
    const response: EventAttendeesPagination = await get(
      `event/${eventId}/event-attendees?page=${page}&pageSize=${pageSize}&dir=desc&sort=created_at`
    )
    return response
  }

  const fetchEventAttendeesMetricsByEventId = async (eventId: string) => {
    const response: IEventMetrics[] = await get(
      `event/${eventId}/attendee/metrics`
    )
    return response
  }

  const updateVFairsUser = async (userId: string, eventId: string) => {
    const response = await put(`event/${eventId}/vfairs/user/${userId}`, {})
    return response
  }

  const fetchAvailableMealTimes = async (): Promise<MealTimes> => {
    const response = await get(`event/meal/times`, { dieSilent: true, useApiKey: true  })
    return response
  }

  const createMealAttendee = async (eventId: string, userId: string, mealAttendee: MealAttendee) => {
    const response = await post(`event/${eventId}/meal/attendee/${userId}`, mealAttendee, { })
    return response
  }

  const fetchMealAttendeeByUserId = async (userId: string) => {
    const response = await get(`event/meal/attendee/${userId}`, { dieSilent: true })
    return response
  }

  return {
    fetchEvent,
    deleteEvent,
    fetchEventWithApiKey,
    createEvent,
    updateEvent,
    registerForEvent,
    unRegisterForEvent,
    fetchSummitEvent,
    fetchSummitAttendeeAndTicket,
    confirmEventAttendee,
    updateEventAttendee,
    loginEngageEvent,
    fetchFilteredEvents,
    fetchRegisteredEvents,
    fetchEventsWithRegistrant,
    fetchEventCounts,
    deleteBulkEvents,
    getAvailableSeats,
    fetchSummitBySubTitle,
    updateVFairsUser,
    fetchEventAttendees,
    isLoading,
    setIsLoading,
    fetchEventAttendeesMetricsByEventId,
    fetchAvailableMealTimes,
    createMealAttendee,
    fetchMealAttendeeByUserId
  } as const
}
